import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import useTailwindBrand from '../../Theme/useTailwindBrand';
import { HELP_PAGE_PATH } from '../App/Home/HelpCard';

interface HelpButtonProps {
  /** AppBar sur fond sombre : icône claire. */
  isDark?: boolean;
  /** Barre mobile : bouton plus compact. */
  size?: 'small' | 'medium';
  /** « pill » : pastille avec le libellé (barre web), sinon icône seule. */
  variant?: 'icon' | 'pill';
}

/**
 * Bouton « Aide et contact » de l'AppBar, à côté de la cloche : visible sur
 * toutes les pages du tableau de bord, prof comme client. Il ouvre la page
 * des coordonnées de l'équipe et du formulaire de contact.
 */
const HelpButton: React.FC<HelpButtonProps> = ({ isDark = false, size = 'medium', variant = 'icon' }) => {
  const { brandConfig } = useTailwindBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const path = HELP_PAGE_PATH();
  const active = location.pathname === path;

  if (variant === 'pill') {
    const primary = brandConfig.colors.primary;
    return (
      <Box
        component="button"
        type="button"
        onClick={() => navigate(path)}
        aria-label="Aide et contact"
        aria-current={active ? 'page' : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1,
          borderRadius: 999,
          border: 'none',
          cursor: 'pointer',
          fontFamily: brandConfig.fontFamily,
          fontSize: '0.8125rem',
          fontWeight: 800,
          color: isDark && !active ? '#fff' : primary,
          backgroundColor: isDark && !active ? 'rgba(255,255,255,0.12)' : `${primary}1A`,
          transition: 'background-color 0.18s ease',
          '&:hover': { backgroundColor: isDark && !active ? 'rgba(255,255,255,0.2)' : `${primary}2E` },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid currentColor', display: 'grid', placeItems: 'center', fontSize: 11, lineHeight: 1 }}
        >
          ?
        </Box>
        Aide et contact
      </Box>
    );
  }

  return (
    <Tooltip title="Aide et contact" arrow>
      <IconButton
        size={size}
        onClick={() => navigate(path)}
        aria-label="Aide et contact"
        aria-current={active ? 'page' : undefined}
        sx={{
          color: active ? brandConfig.colors.primary : isDark ? 'rgba(255,255,255,0.85)' : '#374151',
          borderRadius: '10px',
          backgroundColor: active ? `${brandConfig.colors.primary}12` : 'transparent',
          '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
        }}
      >
        <HelpOutlineRoundedIcon />
      </IconButton>
    </Tooltip>
  );
};

export default HelpButton;
